import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { DocumentService } from 'src/app/services/document/document.service';
import { CryptedBucketInfos, MigrationService } from 'src/app/services/migration/migration.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { environment } from 'src/environments/environment';

export interface NgSelectValue {
  id: string;
  libelle: string;
}

enum MigrationStep {
  ImportOldData,
  MapProfils,
}

interface StepInfos {
  title: string;
}

const MigrationStepInfos: Map<MigrationStep, StepInfos> = new Map([
  [MigrationStep.ImportOldData, { title: `Fichier d'entrée "découverte" de migration` }],
  [MigrationStep.MapProfils, { title: 'Informations de migration' }],
]);

const initialStep = MigrationStep.ImportOldData;

interface MigrationFileContent {
  oldProfils: Array<OldProfil>;
}

interface OldProfil {
  id: any;
  nom: string;
  codeUtil?: number;
}

interface InfosCabinet {
  idCabinet: string;
  authBucket: string;
  ivBucket: string;
}

export interface MigrationConfiguration {
  idCabinet: string;
  responsableCabinet: string;
  associationProfil: Array<MigrationConfigurationProfils>;
  photosPath: string;
  authBucket: string;
  ivBucket: string;
}

export interface MigrationConfigurationProfils {
  oldId: string;
  oldName: string;
  oldCodeUtil?: number;
  newId: string;
}

@Component({
  selector: 'app-migration',
  templateUrl: './migration.component.html',
  styleUrls: [
    './migration.component.scss',
    '../modal.fragment.scss'
  ]
})
export class MigrationComponent implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public closeEmitter = new EventEmitter();

  public header = MigrationStepInfos.get(initialStep).title;

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeModal();
    }
  }

  public currentStep: MigrationStep = initialStep;
  public currentStepTplt = MigrationStep;

  public fileName: string;
  public migrationFile: File;

  public formGroup: FormGroup;
  public formGroupProfils: FormArray;

  public cabinetUsers: Array<NgSelectValue> = [];
  public profilsList: MigrationFileContent;
  public infosCabinet: InfosCabinet;
  public cabinetName: string;

  constructor(
    private _formBuilder: FormBuilder,
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _documentSrv: DocumentService,
    private _migrationSrv: MigrationService,
  ) { }

  ngOnInit(): void {
  }

  public closeModal(): void {
    this.closeEmitter.emit();
  }

  public importInitialMigrationFile(): void {
    const body = document.getElementsByTagName('body')[0];
    const inputElem = document.createElement('input');
    inputElem.type = 'file';
    inputElem.setAttribute('multiple', 'false');
    body.appendChild(inputElem);
    inputElem.click();
    inputElem.addEventListener('change', async (event: any) => {
      this.fileName = (event.target.files[0] as File).name;
      this.migrationFile = event.target.files[0];
      inputElem.remove();
    });
  }

  public cancel(): void {
    const confirmModal = this._modalSrv.openModalConfirmation(
      this._viewContainerRef,
      'Annuler la migration',
      'Êtes-vous sûr de vouloir annuler la migration ?'
    );
    const sub = confirmModal.instance.closeEmitter.subscribe((result: boolean) => {
      if (result) {
        this.closeModal();
      }
      sub.unsubscribe();
    });
  }

  public async nextStep(): Promise<void> {
    await this.changeStep(MigrationStep.MapProfils);
  }

  private async changeStep(step: MigrationStep): Promise<void> {
    if (step === MigrationStep.MapProfils) {
      if (await this._initMapStep() === false) {
        return;
      }
    }

    this.currentStep = step;
    this.header = MigrationStepInfos.get(step).title;
  }

  private async _initMapStep(): Promise<boolean> {
    this.profilsList = await this._parseMigrationFile(this.migrationFile);
    if (this.migrationFile && !this.profilsList) {
      this._modalSrv.openAlert(
        this._viewContainerRef,
        'Erreur de lecture du fichier de migration',
        'Le fichier de migration n\'a pas pu être lu.\nVeuillez vérifier que le fichier est bien au format JSON et est valide.'
      );
      this.migrationFile = undefined;
      this.changeStep(MigrationStep.ImportOldData);
      return false;
    }

    let formArr: FormArray;
    if (this.profilsList?.oldProfils?.length > 1) {
      formArr = new FormArray([]);
      for (const profil of this.profilsList.oldProfils) {
        formArr.push(this._formBuilder.group({
          oldProfilLibelle: [decodeURIComponent(escape(profil.nom))],
          oldProfilId: [profil.id],
          oldCodeUtil: [profil.codeUtil],
          newProfilId: [undefined, Validators.required],
        }));
      }

      this.formGroupProfils = formArr;
    }

    this.formGroup = this._formBuilder.group({
      responsableCabinet: [undefined, Validators.required],
      photosPath: [undefined],
      profils: this.formGroupProfils,
    });
  }

  private async _parseMigrationFile(file: File): Promise<MigrationFileContent | undefined> {
    try {
      if (file) {
        const migrationFileB64 = await this._documentSrv.convertFileToBase64(file);
        const migrationFile = JSON.parse(atob(migrationFileB64))

        if (!migrationFile.oldProfils || !Array.isArray(migrationFile.oldProfils) || migrationFile.oldProfils.length < 1) {
          return undefined;
        }

        return migrationFile;
      } else {
        return undefined;
      }
    } catch (error) {
      return undefined;
    }
  }

  public async submitMigrationConfiguration(): Promise<void> {
    const responsableCabinet: string = this.formGroup.get('responsableCabinet').value;
    const photosPath: string = this.formGroup.get('photosPath').value;

    const association: Array<MigrationConfigurationProfils> = [];
    if (this.formGroup.get('profils').value) {

      const profils: Array<{
        oldProfilLibelle: string;
        oldProfilId: string;
        newProfilId?: string;
        oldCodeUtil?: number;
      }> = this.formGroup.get('profils').value;

      if (profils.find(profil => !profil.newProfilId)) {
        this._modalSrv.openAlert(
          this._viewContainerRef, 'Erreur de sélection de profil',
          'Vous devez sélectionner un nouveau profil pour chaque profil existant.'
        );
        return;
      }

      for (const profil of profils) {
        association.push({
          oldId: profil.oldProfilId,
          oldName: profil.oldProfilLibelle,
          oldCodeUtil: profil.oldCodeUtil,
          newId: profil.newProfilId,
        });
      }
    }

    const bucket = await this._getBucketCryptedInfos(this.infosCabinet.idCabinet);

    const config: MigrationConfiguration = {
      idCabinet: this.infosCabinet?.idCabinet || undefined,
      responsableCabinet,
      associationProfil: association,
      photosPath,
      authBucket: bucket?.authBucket || null,
      ivBucket: bucket?.ivBucket || null,
    };

    await this._downloadMigrationConfig(config);

    this.closeModal();
  }

  private async _downloadMigrationConfig(migrationConfig: MigrationConfiguration): Promise<void> {
    const objJsonStr = JSON.stringify(migrationConfig);
    const file = window.URL.createObjectURL(new Blob([objJsonStr]));

    const link = document.createElement('a');
    link.href = file;
    link.download = this._getConfigFilename();
    link.click();
  }

  private _getConfigFilename(): string {
    const _removeSpaces = (name: string) => {
      return name.replace(/\s/g, '-');
    }
    return `config-migration_${_removeSpaces(this.cabinetName)}_${moment().format('DD-MM-YYYY')}.json`;
  }

  private async _getBucketCryptedInfos(cabinetId: string): Promise<CryptedBucketInfos> {
    try {
      const res = await this._migrationSrv.getBucketCryptedInfos(cabinetId);
      return res;
    } catch (err) {
      throw err;
    }
  }

}
